import type { IUndoRedo } from "@models/project/IUndoRedo";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import type { IPermissionsModel } from "./IPermissionsModel";

/**
 * What the EDITOR does when the user hands the presenter role to Spark
 * (`IPermissionsModel.isPresenterDelegated` — a workflow run or an author's
 * preview), beyond the predicates going false. Two things the predicates
 * cannot do on their own, because both are state left over from BEFORE the
 * hand-over:
 *
 * - **The selection is cleared.** It was made while the user could edit, and
 *   every command that acts on "the selection" would otherwise find it waiting.
 *   The permission gates on those commands are the guard; this removes the
 *   target, and the transform gizmo that was attached to it with it.
 * - **Undo is paused** until the delegation ends (`IUndoRedo.suspend`). The
 *   run's own changes are temporary and the run puts them back, so an entry
 *   recorded for one — a scripted explode does record — could be replayed by a
 *   later Undo outside the run's no-save scope, where it would SAVE and
 *   broadcast. The author's history from before the run is kept and returns.
 *
 * Disarming the active tool is NOT here: that is a permission rule in general,
 * not a delegation one, and the toolbar model owns it
 * (`InMemoryToolbarModel.demoteDeniedActiveTool`).
 *
 * Wired once at the composition root. Returns the unsubscribe.
 */
export function bindPresenterDelegationEffects(deps: {
  readonly permissions: IPermissionsModel;
  readonly selection: ISelectionModel;
  readonly undoRedo: IUndoRedo;
}): () => void {
  let releaseUndo: (() => void) | null = null;

  const sync = (): void => {
    const delegated = deps.permissions.isPresenterDelegated;
    if (delegated && releaseUndo === null) {
      releaseUndo = deps.undoRedo.suspend();
      deps.selection.clear();
    } else if (!delegated && releaseUndo !== null) {
      const release = releaseUndo;
      releaseUndo = null;
      release();
    }
  };

  const off = deps.permissions.onPermissionsChanged.on(sync);
  sync();
  return () => {
    off();
    releaseUndo?.();
    releaseUndo = null;
  };
}
